import { useQuery } from "@tanstack/react-query";

import { createSearchMatcher } from "./searchMatcher";
import { useLookupBarcodeHook } from "./staverton.openapi/open-retail";

export interface StockLookupResponse {
  records: StockLookupResponseRecord[];
}

export interface StockLookupResponseRecord {
  Stock_Code: number;
  Full_Desc: string;
  Barcode: string;
  Type: number;
  Outer_Unit: number;
  Retail_Stock_Balance: number;
  Warehouse_Stock_Balance: number;
  Last_Sell_Date: string;
  Supplier_Ref: string;
  Stocktake_Freeze_Retail: number;
  BandA_Sell: string;
  RRP: string;
  Preferred_Supplier: string;
  Date_Time_Stamp?: string;
  Deleted?: 1 | 0;
  Suspended?: 1 | 0;
  Discarded?: 1 | 0;
}

export function isIgnored(result: StockLookupResponseRecord) {
  return !!result.Deleted || !!result.Suspended || !!result.Discarded;
}

export type StockFilter = "none" | "excludeZero" | "excludeNonPositive";

const stockFilterFn: Record<StockFilter, (record: StockLookupResponseRecord) => boolean> = {
  none: () => true,
  excludeZero: (record) => record.Retail_Stock_Balance !== 0,
  excludeNonPositive: (record) => record.Retail_Stock_Balance > 0,
};

export function useStockLookup(
  {
    code,
    checkStockCode,
    searchDescription,
    stockFilter = "none",
  }: { code?: string; checkStockCode?: boolean; searchDescription?: string; stockFilter?: StockFilter },
  { enabled } = { enabled: true }
) {
  const lookupBarcode = useLookupBarcodeHook();
  if (searchDescription === undefined && (code === undefined || Number.isNaN(parseInt(code)))) {
    searchDescription = code;
    code = undefined;
  }

  return useQuery({
    queryKey: ["stockLookup", code, checkStockCode, searchDescription, stockFilter],
    queryFn: async () => {
      const searchDescriptionMatcher = searchDescription ? createSearchMatcher(searchDescription) : null;
      const result = await lookupBarcode({
        barcode: code,
        checkStockCode: checkStockCode ?? false,
        searchDescription: searchDescriptionMatcher?.serverSearchPart,
      });
      const response = JSON.parse(result.data.response) as StockLookupResponse;

      const MAX_UNFILTERED_RECORDS = 10_000;
      const MAX_FILTERED_RECORDS = 500;

      const filteredRecords = response.records
        .values()
        .take(MAX_UNFILTERED_RECORDS)
        .filter(stockFilterFn[stockFilter])
        .map((i) => ({
          item: i,
          searchDescriptionScore: searchDescriptionMatcher?.matchScore(i.Full_Desc),
        }))
        // Include both undefined, and scores > 0
        .filter((i) => i.searchDescriptionScore !== 0)
        .take(MAX_FILTERED_RECORDS)
        .toArray();
      const orderedRecords = filteredRecords
        .slice(0, 100)
        .sort((a, b) => {
          // Sort by whether the item is deleted, suspended or discarded
          const aIsIgnored = isIgnored(a.item);
          const bIsIgnored = isIgnored(b.item);
          if (aIsIgnored && !bIsIgnored) return 1;
          if (!aIsIgnored && bIsIgnored) return -1;

          if (searchDescriptionMatcher) {
            const scoreDiff = b.searchDescriptionScore! - a.searchDescriptionScore!;
            if (scoreDiff !== 0) return scoreDiff;
          }

          // Sort by date descending
          const timeDiff =
            new Date(b.item.Date_Time_Stamp ?? 0).getTime() - new Date(a.item.Date_Time_Stamp ?? 0).getTime();
          if (timeDiff !== 0) return timeDiff;

          return 0;
        })
        .map((i) => i.item);
      return {
        records: orderedRecords,
        totalRecordsDisplay:
          filteredRecords.length === MAX_FILTERED_RECORDS
            ? `${MAX_FILTERED_RECORDS}+`
            : response.records.length > MAX_UNFILTERED_RECORDS
              ? `${filteredRecords.length}+`
              : filteredRecords.length > orderedRecords.length
                ? filteredRecords.length.toString()
                : null,
      };
    },
    enabled,
  });
}
